import { useEffect, useRef } from 'react'
import { NOSTR_SIGNING_ERROR } from '@/lib/nostr'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { InfiniteData, QueryKey } from '@tanstack/react-query'
import { useAuth } from '@/hooks/useAuth'
import { getActiveBlueskySessionDid } from '@/contexts/AuthContext'
import { useAppView } from '@/hooks/useAppView'
import { useToast } from '@/hooks/useToast'
import { getAdapter } from '@/adapters/registry'
import { MASTODON_PROPAGATION_DELAY_MS } from '@/constants/mastodon'
import {
    postsQueryKey,
    PINNED_POST_FEED_FILTER,
    PROFILE_FEED_PAGE_SIZE,
    type PostsFeedSource,
} from '@/lib/postsQueryKeys'
import { applyOptimisticPin } from '@/lib/pinnedSlotReorder'
import {
    beginPinOperation,
    currentPinEpoch,
    endPinOperation,
    scheduleEndPinOperation,
    PIN_CONFIRM_RECONCILE_GRACE_MS,
} from '@/lib/pinnedSlotSync'
import { confirmPinnedPostCommit } from '@/services/pinnedPostCommit'
import { MastodonStatusActionError } from '@/services/mastodonService'
import { StalePinnedPostError } from '@/services/profileService'
import { strings } from '@/strings'
import type { AuthorFeedFilter, Post, PostAuthor, PostsPage } from '@/types/posts'
import type { Thread, ThreadPost } from '@/types/thread'
import type { AdapterSession } from '@/adapters/types'
import type { Platform } from '@/contexts/ProtocolContext/ProtocolContext'

/**
 * Which feed the user is actually looking at when they take a pin action.
 *
 * AT Protocol needs no such hint — pins are observable on the Posts tab and
 * nowhere else, so the affordance only exists there (see `activeFeedKey`
 * below). Mastodon is different: it serialises `pinned` on any authenticated
 * read of your own statuses, so Pin/Unpin is live on the Replies tab too, and
 * the optimistic flip has to land in whichever tab's cache entry the visible
 * list is reading from.
 */
export interface ActiveFeedDescriptor {
    feedSource: PostsFeedSource
    authorFeedFilter: AuthorFeedFilter | undefined
}

export function usePostEngagement(
    identifier: string | undefined,
    currentUser?: PostAuthor,
    options?: { queryKey?: QueryKey; activeFeed?: ActiveFeedDescriptor }
) {
    const { session, mastodonSession, pixelfedSession, nostrSession, requestNostrReauth } =
        useAuth()
    const { showToast } = useToast()
    // The AppView the feed reads from. The pinned-post confirmation polls the same
    // endpoint (on a cache-bypassing clone), so a non-default AppView selection is
    // confirmed against itself rather than against Bluesky's default.
    const { publicAgent, appView } = useAppView()
    const queryClient = useQueryClient()
    const queryKey = options?.queryKey ?? ['posts', identifier]
    const propagationTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
    // For AT Protocol, pins are only observable on the pinned-slot feed, so that is
    // the one cache entry the confirmed page belongs in — built from the same builder
    // usePosts uses, because an exact-key setQueryData silently no-ops on any drift.
    const pinnedFeedKey = postsQueryKey({
        identifier,
        feedSource: 'authorFeed',
        authorFeedFilter: PINNED_POST_FEED_FILTER,
        hasSession: !!session,
        hasPixelfedSession: !!pixelfedSession,
        appView,
    })
    /**
     * The cache entry the *visible* list is reading from, when the caller says so.
     *
     * For AT Protocol this is always `pinnedFeedKey` — `usePosts` only asks the
     * AppView for pins on the Posts tab, so `isPinned` (and with it the whole
     * Pin/Unpin affordance in `PostCard`) is undefined everywhere else and the
     * action cannot be taken from another tab at all.
     *
     * Mastodon has no such gate: every authenticated read of your own statuses
     * carries `pinned`, so the Replies tab offers Pin/Unpin too — and it is read
     * from a `collection`/`POSTS_WITH_REPLIES` cache entry, which `pinnedFeedKey`
     * (hardcoded to `authorFeed`/`POSTS_NO_REPLIES`) does not match. Writing the
     * optimistic flip there produced no visible feedback at all; the user saw
     * nothing until the post-success invalidation's refetch came back.
     */
    const activeFeedKey = options?.activeFeed
        ? postsQueryKey({
              identifier,
              feedSource: options.activeFeed.feedSource,
              authorFeedFilter: options.activeFeed.authorFeedFilter,
              hasSession: !!session,
              hasPixelfedSession: !!pixelfedSession,
              appView,
          })
        : pinnedFeedKey

    /**
     * Which cache entry a pin on `post` writes to, and therefore the identity the
     * epoch and the reconcile window are both keyed on.
     *
     * This has to be the key the *visible* query actually reads from, not a fixed
     * AT shape. `usePosts` consumes the window through `structuralSharing` keyed on
     * its own query key, so a window opened under a key nothing reads is bookkeeping
     * that never fires — which is exactly what happened to Mastodon while this was
     * hardcoded to `pinnedFeedKey`: a concurrent `['posts', identifier]` invalidation
     * during the pin's own network round-trip landed a pre-pin page on the Replies
     * tab with nothing to reconcile it.
     */
    function pinSyncKeyFor(post: Post): string {
        return JSON.stringify(post.isActivityPub ? activeFeedKey : pinnedFeedKey)
    }

    useEffect(() => {
        return () => {
            if (propagationTimer.current) clearTimeout(propagationTimer.current)
        }
    }, [])

    /** Resolve adapter and session based on the post's own platform (not selected platform). */
    function getPostAdapterAndSession(post: Post): {
        adapter: ReturnType<typeof getAdapter>
        adapterSession: AdapterSession
    } {
        let platform: Platform
        if (post.protocol === 'nostr') {
            platform = 'nostr'
        } else if (post.isActivityPub) {
            if (!post.protocol) {
                console.warn(
                    '[usePostEngagement] ActivityPub post missing protocol field, falling back to mastodon',
                    post.uri
                )
            }
            platform = (post.protocol as Platform | undefined) ?? 'mastodon'
        } else {
            platform = 'bluesky'
            // Fall back to Mastodon for bridged AT Protocol posts when no Bluesky session
            if (!session && mastodonSession) {
                platform = 'mastodon'
            }
        }
        const adapter = getAdapter(platform)
        const adapterSession =
            platform === 'bluesky'
                ? session
                : platform === 'nostr'
                  ? nostrSession
                  : platform === 'pixelfed'
                    ? pixelfedSession
                    : mastodonSession
        if (!adapterSession) throw new Error('Cannot perform action: no session for this platform')
        return { adapter, adapterSession }
    }

    const updatePostInCache = (postUri: string, updater: (post: Post) => Post) => {
        // Update all posts infinite query caches matching this identifier (prefix match
        // covers all feedSource/filter/session variants of the query key)
        const updatePost = (post: Post): Post => {
            // Update top-level post
            if (post.uri === postUri) return updater(post)
            // Update nested parentPost
            if (post.parentPost?.uri === postUri) {
                return { ...post, parentPost: updater(post.parentPost) }
            }
            return post
        }

        queryClient.setQueriesData<InfiniteData<PostsPage>>({ queryKey }, oldData => {
            if (!oldData?.pages) return oldData
            return {
                ...oldData,
                pages: oldData.pages.map(page => ({
                    ...page,
                    posts: page.posts.map(updatePost),
                })),
            }
        })
        // Update reposts single query cache
        queryClient.setQueriesData<PostsPage>({ queryKey: ['reposts', identifier] }, oldData => {
            if (!oldData) return oldData
            return {
                ...oldData,
                posts: oldData.posts.map(updatePost),
            }
        })
        // Mixed-in custom feed posts (useMixedTimeline) live in a separate cache from
        // the standard timeline, so optimistic updates have to reach this one too.
        queryClient.setQueriesData<PostsPage>({ queryKey: ['customFeed'] }, oldData => {
            if (!oldData) return oldData
            return {
                ...oldData,
                posts: oldData.posts.map(updatePost),
            }
        })
        // Update likes caches
        queryClient.setQueriesData<InfiniteData<PostsPage>>({ queryKey: ['likes'] }, oldData => {
            if (!oldData?.pages) return oldData
            return {
                ...oldData,
                pages: oldData.pages.map(page => ({
                    ...page,
                    posts: page.posts.map(updatePost),
                })),
            }
        })
        // Update favourites caches
        queryClient.setQueriesData<InfiniteData<PostsPage>>(
            { queryKey: ['favourites'] },
            oldData => {
                if (!oldData?.pages) return oldData
                return {
                    ...oldData,
                    pages: oldData.pages.map(page => ({
                        ...page,
                        posts: page.posts.map(updatePost),
                    })),
                }
            }
        )
        // Update bookmarks caches
        queryClient.setQueriesData<InfiniteData<PostsPage>>(
            { queryKey: ['bookmarks'] },
            oldData => {
                if (!oldData?.pages) return oldData
                return {
                    ...oldData,
                    pages: oldData.pages.map(page => ({
                        ...page,
                        posts: page.posts.map(updatePost),
                    })),
                }
            }
        )
        // Update notification quote posts Map cache
        queryClient.setQueriesData<Map<string, Post>>(
            { queryKey: ['notificationQuotePosts'] },
            oldData => {
                if (!oldData) return oldData
                const updated = new Map(oldData)
                for (const [uri, post] of updated) {
                    if (post.uri === postUri) {
                        updated.set(uri, updater(post))
                    }
                }
                return updated
            }
        )
        // Update thread caches (main post, replies, parent chain)
        const updateThreadData = (oldThread: Thread | undefined) => {
            if (!oldThread) return oldThread
            const updateThreadPost = (tp: ThreadPost): ThreadPost =>
                tp.post.uri === postUri ? { ...tp, post: updater(tp.post) } : tp
            return {
                ...oldThread,
                post: updateThreadPost(oldThread.post),
                parentChain: oldThread.parentChain.map(updateThreadPost),
                replies: oldThread.replies.map(updateThreadPost),
            }
        }
        queryClient.setQueriesData<Thread>({ queryKey: ['thread'] }, updateThreadData)
        queryClient.setQueriesData<Thread>({ queryKey: ['ap-thread'] }, updateThreadData)
    }

    const toggleLike = useMutation({
        mutationFn: async (post: Post) => {
            const { adapter, adapterSession } = getPostAdapterAndSession(post)
            if (post.likeUri) {
                await adapter.unlikePost(adapterSession, post)
                return { uri: null, liked: false }
            } else {
                return adapter.likePost(adapterSession, post)
            }
        },
        onMutate: post => {
            updatePostInCache(post.uri, p => ({
                ...p,
                likeUri: p.likeUri ? null : 'pending',
                metrics: {
                    ...p.metrics,
                    likeCount: p.likeUri
                        ? Math.max(0, p.metrics.likeCount - 1)
                        : p.metrics.likeCount + 1,
                },
            }))
        },
        onSuccess: (result, post) => {
            updatePostInCache(post.uri, p => ({
                ...p,
                likeUri: result.uri,
            }))
            // Also update the single-post cache (use correct session key per protocol)
            const postSessionKey = post.isActivityPub
                ? !!(mastodonSession ?? pixelfedSession)
                : !!session
            queryClient.setQueryData<Post>(['post', post.uri, postSessionKey], oldPost =>
                oldPost ? { ...oldPost, likeUri: result.uri } : oldPost
            )
        },
        onError: error => {
            const message = error instanceof Error ? error.message : ''
            if (message.includes(NOSTR_SIGNING_ERROR)) {
                void requestNostrReauth()
                return
            }
            void queryClient.invalidateQueries({ queryKey })
            console.error('[usePostEngagement] like failed:', error)
            showToast('Failed to update like', 'error')
        },
    })

    const toggleRepost = useMutation({
        mutationFn: async (post: Post) => {
            const { adapter, adapterSession } = getPostAdapterAndSession(post)
            if (post.repostUri) {
                await adapter.unrepostPost(adapterSession, post)
                return { uri: null, reposted: false }
            } else {
                return adapter.repostPost(adapterSession, post)
            }
        },
        onMutate: post => {
            updatePostInCache(post.uri, p => {
                const isUnrepost = !!p.repostUri
                return {
                    ...p,
                    repostUri: isUnrepost ? null : 'pending',
                    // Toggle repost banner optimistically
                    ...(isUnrepost
                        ? { isRepost: false, repostedBy: undefined }
                        : currentUser && { isRepost: true, repostedBy: currentUser }),
                    metrics: {
                        ...p.metrics,
                        repostCount: isUnrepost
                            ? Math.max(0, p.metrics.repostCount - 1)
                            : p.metrics.repostCount + 1,
                    },
                }
            })
        },
        onSuccess: (result, post) => {
            updatePostInCache(post.uri, p => ({
                ...p,
                repostUri: result.uri,
                ...(result.uri
                    ? currentUser && { isRepost: true, repostedBy: currentUser }
                    : { isRepost: false, repostedBy: undefined }),
            }))
            // Also update the single-post cache (use correct session key per protocol)
            const postSessionKey = post.isActivityPub
                ? !!(mastodonSession ?? pixelfedSession)
                : !!session
            queryClient.setQueryData<Post>(['post', post.uri, postSessionKey], oldPost =>
                oldPost ? { ...oldPost, repostUri: result.uri } : oldPost
            )
            // Optimistically insert/remove the repost in the current user's feed
            // Skip when viewing own feed — updatePostInCache already handled it there
            const isOwnFeed = identifier === currentUser?.did || identifier === currentUser?.handle
            if (currentUser?.did && !isOwnFeed) {
                const insertRepost = (oldData: InfiniteData<PostsPage> | undefined) => {
                    if (!oldData) return oldData
                    if (result.uri) {
                        // Insert repost at the top of the first page
                        const repostEntry: Post = {
                            ...post,
                            repostUri: result.uri,
                            isRepost: true,
                            repostedBy: currentUser,
                            createdAt: new Date().toISOString(),
                        }
                        return {
                            ...oldData,
                            pages: oldData.pages.map((page, i) =>
                                i === 0 ? { ...page, posts: [repostEntry, ...page.posts] } : page
                            ),
                        }
                    } else {
                        // Remove the repost entry from cache
                        return {
                            ...oldData,
                            pages: oldData.pages.map(page => ({
                                ...page,
                                posts: page.posts.filter(p => !(p.uri === post.uri && p.isRepost)),
                            })),
                        }
                    }
                }
                queryClient.setQueriesData<InfiniteData<PostsPage>>(
                    { queryKey: ['posts', currentUser.did] },
                    insertRepost
                )
                if (currentUser.handle) {
                    queryClient.setQueriesData<InfiniteData<PostsPage>>(
                        { queryKey: ['posts', currentUser.handle] },
                        insertRepost
                    )
                }
            }
        },
        onError: error => {
            const message = error instanceof Error ? error.message : ''
            if (message.includes(NOSTR_SIGNING_ERROR)) {
                void requestNostrReauth()
                return
            }
            void queryClient.invalidateQueries({ queryKey })
            console.error('[usePostEngagement] repost failed:', error)
            showToast('Failed to update repost', 'error')
        },
    })

    const toggleBookmark = useMutation({
        mutationFn: async (post: Post) => {
            const { adapter, adapterSession } = getPostAdapterAndSession(post)
            if (post.bookmarkUri) {
                if (!adapter.unbookmarkPost)
                    throw new Error('Unbookmark not supported for this protocol')
                await adapter.unbookmarkPost(adapterSession, post)
                return { uri: null, bookmarked: false }
            } else {
                if (!adapter.bookmarkPost)
                    throw new Error('Bookmark not supported for this protocol')
                return adapter.bookmarkPost(adapterSession, post)
            }
        },
        onMutate: async post => {
            await queryClient.cancelQueries({ queryKey: ['bookmarks'] })
            const previousBookmarksData = queryClient.getQueriesData<InfiniteData<PostsPage>>({
                queryKey: ['bookmarks'],
            })
            updatePostInCache(post.uri, p => ({
                ...p,
                bookmarkUri: p.bookmarkUri ? null : 'pending',
            }))
            return { previousBookmarksData }
        },
        onSuccess: (result, post) => {
            updatePostInCache(post.uri, p => ({
                ...p,
                bookmarkUri: result.uri,
            }))
            // When unbookmarking, remove the post from the bookmarks feed
            if (!result.uri) {
                queryClient.setQueriesData<InfiniteData<PostsPage>>(
                    { queryKey: ['bookmarks'] },
                    oldData => {
                        if (!oldData?.pages) return oldData
                        return {
                            ...oldData,
                            pages: oldData.pages.map(page => ({
                                ...page,
                                posts: page.posts.filter(p => p.uri !== post.uri),
                            })),
                        }
                    }
                )
            }
        },
        onError: (error, _post, context) => {
            console.error('[usePostEngagement] bookmark failed:', error)
            if (context?.previousBookmarksData) {
                for (const [key, data] of context.previousBookmarksData) {
                    queryClient.setQueryData(key, data)
                }
            }
            void queryClient.invalidateQueries({ queryKey: ['bookmarks'] })
            void queryClient.invalidateQueries({ queryKey })
            showToast('Failed to update bookmark', 'error')
        },
    })

    const removePost = useMutation({
        mutationFn: async (post: Post) => {
            const { adapter, adapterSession } = getPostAdapterAndSession(post)
            await adapter.deletePost(adapterSession, post)
        },
        onMutate: async post => {
            await queryClient.cancelQueries({ queryKey })
            await queryClient.cancelQueries({ queryKey: ['thread'] })
            await queryClient.cancelQueries({ queryKey: ['ap-thread'] })
            await queryClient.cancelQueries({ queryKey: ['likes'] })
            await queryClient.cancelQueries({ queryKey: ['favourites'] })
            await queryClient.cancelQueries({ queryKey: ['bookmarks'] })

            const previousFeedData = queryClient.getQueriesData<InfiniteData<PostsPage>>({
                queryKey,
            })
            const previousThreadData = queryClient.getQueriesData<Thread>({ queryKey: ['thread'] })
            const previousAPThreadData = queryClient.getQueriesData<Thread>({
                queryKey: ['ap-thread'],
            })
            const previousLikesData = queryClient.getQueriesData<InfiniteData<PostsPage>>({
                queryKey: ['likes'],
            })
            const previousFavouritesData = queryClient.getQueriesData<InfiniteData<PostsPage>>({
                queryKey: ['favourites'],
            })
            const previousBookmarksData = queryClient.getQueriesData<InfiniteData<PostsPage>>({
                queryKey: ['bookmarks'],
            })

            // Optimistically remove from feed caches (skip thread caches which lack .pages)
            const removeFromPages = (oldData: InfiniteData<PostsPage> | undefined) => {
                if (!oldData?.pages) return oldData
                return {
                    ...oldData,
                    pages: oldData.pages.map(page => ({
                        ...page,
                        posts: page.posts.filter(p => p.uri !== post.uri),
                    })),
                }
            }
            queryClient.setQueriesData<InfiniteData<PostsPage>>({ queryKey }, removeFromPages)
            // Optimistically remove from likes caches
            queryClient.setQueriesData<InfiniteData<PostsPage>>(
                { queryKey: ['likes'] },
                removeFromPages
            )
            // Optimistically remove from favourites caches
            queryClient.setQueriesData<InfiniteData<PostsPage>>(
                { queryKey: ['favourites'] },
                removeFromPages
            )
            // Optimistically remove from bookmarks caches
            queryClient.setQueriesData<InfiniteData<PostsPage>>(
                { queryKey: ['bookmarks'] },
                removeFromPages
            )
            // Optimistically remove from thread caches
            const removeFromThread = (oldThread: Thread | undefined) => {
                if (!oldThread) return oldThread
                const wasReply = oldThread.replies.some(r => r.post.uri === post.uri)
                // Decrement replyCount on the parent post only when deleting a reply
                const parentUri = post.replyParentUri
                const decrementReplyCount = (p: Post): Post =>
                    wasReply && parentUri && p.uri === parentUri
                        ? {
                              ...p,
                              metrics: {
                                  ...p.metrics,
                                  replyCount: Math.max(0, p.metrics.replyCount - 1),
                              },
                          }
                        : p
                return {
                    ...oldThread,
                    post: { ...oldThread.post, post: decrementReplyCount(oldThread.post.post) },
                    parentChain: oldThread.parentChain
                        .filter(r => r.post.uri !== post.uri)
                        .map(r =>
                            r.post.uri === parentUri
                                ? { ...r, post: decrementReplyCount(r.post) }
                                : r
                        ),
                    replies: oldThread.replies
                        .filter(r => r.post.uri !== post.uri)
                        .map(r =>
                            r.post.uri === parentUri
                                ? { ...r, post: decrementReplyCount(r.post) }
                                : r
                        ),
                    replyCount: wasReply
                        ? Math.max(0, oldThread.replyCount - 1)
                        : oldThread.replyCount,
                }
            }
            queryClient.setQueriesData<Thread>({ queryKey: ['thread'] }, removeFromThread)
            queryClient.setQueriesData<Thread>({ queryKey: ['ap-thread'] }, removeFromThread)

            return {
                previousFeedData,
                previousThreadData,
                previousAPThreadData,
                previousLikesData,
                previousFavouritesData,
                previousBookmarksData,
            }
        },
        onSuccess: (_data, post) => {
            showToast('Post deleted', 'success')
            // Mastodon instances may take a moment to propagate the deletion.
            // Delay the cache invalidation so a refetch doesn't resurrect the post.
            if (post.isActivityPub) {
                propagationTimer.current = setTimeout(() => {
                    void queryClient.invalidateQueries({ queryKey })
                    void queryClient.invalidateQueries({ queryKey: ['thread'] })
                    void queryClient.invalidateQueries({ queryKey: ['ap-thread'] })
                }, MASTODON_PROPAGATION_DELAY_MS)
            }
        },
        onError: (_error, _post, context) => {
            console.error('[usePostEngagement] delete failed:', _error)
            // Instant rollback from snapshots
            if (context?.previousFeedData) {
                for (const [key, data] of context.previousFeedData) {
                    queryClient.setQueryData(key, data)
                }
            }
            if (context?.previousThreadData) {
                for (const [key, data] of context.previousThreadData) {
                    queryClient.setQueryData(key, data)
                }
            }
            if (context?.previousAPThreadData) {
                for (const [key, data] of context.previousAPThreadData) {
                    queryClient.setQueryData(key, data)
                }
            }
            if (context?.previousLikesData) {
                for (const [key, data] of context.previousLikesData) {
                    queryClient.setQueryData(key, data)
                }
            }
            if (context?.previousFavouritesData) {
                for (const [key, data] of context.previousFavouritesData) {
                    queryClient.setQueryData(key, data)
                }
            }
            if (context?.previousBookmarksData) {
                for (const [key, data] of context.previousBookmarksData) {
                    queryClient.setQueryData(key, data)
                }
            }
            // Refetch to sync with server after rollback
            void queryClient.invalidateQueries({ queryKey })
            void queryClient.invalidateQueries({ queryKey: ['thread'] })
            void queryClient.invalidateQueries({ queryKey: ['ap-thread'] })
            void queryClient.invalidateQueries({ queryKey: ['likes'] })
            void queryClient.invalidateQueries({ queryKey: ['favourites'] })
            void queryClient.invalidateQueries({ queryKey: ['bookmarks'] })
            showToast('Failed to delete post', 'error')
        },
    })

    const togglePin = useMutation({
        mutationFn: async (post: Post) => {
            const { adapter, adapterSession } = getPostAdapterAndSession(post)
            if (post.isPinned) {
                if (!adapter.unpinPost) throw new Error('Unpin not supported for this protocol')
                await adapter.unpinPost(adapterSession, post)
            } else {
                if (!adapter.pinPost) throw new Error('Pin not supported for this protocol')
                await adapter.pinPost(adapterSession, post)
            }
        },
        onMutate: async (post: Post) => {
            // For AT Protocol this is the pinned-slot feed and nothing else: pins are a
            // Posts-tab-only affordance (see `isOwnPinnedSlotFeed` in usePosts.ts), so
            // the optimistic flip must not touch every filter's query under this
            // identifier. For Mastodon it is whichever tab the action was taken from —
            // see `activeFeedKey`.
            const optimisticKey = post.isActivityPub ? activeFeedKey : pinnedFeedKey
            // The window and the epoch live on the key this write targets, captured
            // here rather than re-derived later: a tab switch between `onMutate` and
            // the terminal state would otherwise close (or fail to close) the wrong key.
            const pinSyncKey = pinSyncKeyFor(post)
            await queryClient.cancelQueries({ queryKey: optimisticKey, exact: true })

            const previousFeedData = queryClient.getQueriesData<InfiniteData<PostsPage>>({
                queryKey: optimisticKey,
                exact: true,
            })

            const next = !post.isPinned

            // Open the operation on this pin's query key. Two things hang off it:
            //
            // - the epoch, which supersedes any older commit-confirmation chain for the
            //   same key (including one orphaned by an unmount, which a mount-scoped ref
            //   could not recognize as stale);
            // - the reconcile window, which is what stops an *unrelated* refetch — a
            //   reconnect, a remount on this `staleTime: 0` feed, a pull-to-refresh, or
            //   any `['posts', identifier]` invalidation from elsewhere in the app —
            //   from landing pre-pin server data over the state the user was just told
            //   succeeded. See `lib/pinnedSlotSync.ts`.
            //
            // `session?.did` rides along so the window can be suppressed after an
            // account switch: the query key carries `identifier` and `hasSession` but
            // not *which* account is signed in, so account B viewing A's profile
            // resolves to the very entry A pinned from.
            const pinEpoch = beginPinOperation(pinSyncKey, post, next, session?.did)
            /**
             * Move the post to the pinned slot, not just its banner.
             *
             * Clicking Pin has to put the post at the top of the Posts tab in the same
             * frame as the click. An earlier round flipped `pinnedEntry` in place so the
             * banner at least appeared, but the row stayed put and only jumped to the top
             * seconds later when the AppView confirmation landed — reading as two
             * unrelated events rather than one action.
             *
             * `applyOptimisticPin` does the reorder and documents why it is key-safe
             * (short version: page one holds exactly one row per URI, and the one case
             * that could produce two identically-keyed rows — re-inserting a demoted row
             * that a deeper page still carries — is guarded there).
             *
             * Still scoped to AT Protocol: Mastodon returns the pinned copy *and* the
             * chronological copy and never dedupes, and its write is synchronous anyway.
             */
            queryClient.setQueriesData<InfiniteData<PostsPage>>(
                { queryKey: optimisticKey, exact: true },
                oldData => {
                    if (!oldData?.pages) return oldData
                    return { ...oldData, pages: applyOptimisticPin(oldData.pages, post, next) }
                }
            )

            return { previousFeedData, pinEpoch, pinSyncKey }
        },
        onSuccess: (_data, post, context) => {
            const wasPinned = !!post.isPinned
            showToast(wasPinned ? strings.posts.postUnpinned : strings.posts.postPinned, 'success')

            if (post.isActivityPub) {
                // Mastodon's pin/unpin response *is* the commit — the write is synchronous
                // on the user's own instance, with no federation hop in the read path. So
                // there is nothing for the refetch below to race: close the window first
                // and let the invalidation's result land as-is.
                if (context) endPinOperation(context.pinSyncKey, context.pinEpoch)
                void queryClient.invalidateQueries({ queryKey })
                return
            }

            // AT Protocol: the PDS accepted the write, but getAuthorFeed reads from the
            // AppView, which indexes asynchronously. Confirm against that same AppView
            // feed — not the PDS — and then write the confirmed page straight into the
            // cache. `invalidateQueries` here would reissue a byte-identical GET to the
            // AppView's public API, which answers `cache-control: public, max-age=30`
            // from behind a CDN: for up to 30s the refetch is served the stale pre-pin
            // body with no network request at all, the banner never moves, and usePosts
            // re-derives isPinned:false so the menu label reverts on a pin that worked.
            //
            // On a full timeout we keep the optimistic state and do not refetch: a blind
            // refetch would pull the pre-pin feed and visually revert a pin the user has
            // already been told succeeded, and refetchOnWindowFocus is globally disabled
            // (lib/queryClient.ts) so nothing would repair it. The next user-initiated
            // fetch picks up the real state.
            if (!session || !identifier) {
                // Nothing is going to confirm this write, so the reconcile window must
                // not stay open — it would re-apply this expectation over every later
                // refetch of the feed, forever.
                if (context) endPinOperation(context.pinSyncKey, context.pinEpoch)
                return
            }
            // The AT branch always targets the pinned-slot feed (see `pinSyncKeyFor`),
            // which is also where the confirmed page is written below.
            const pinSyncKey = context?.pinSyncKey ?? JSON.stringify(pinnedFeedKey)
            // The DID this pin was initiated under — compared against the live active
            // account (from AuthContext's `getActiveBlueskySessionDid`, which is kept
            // current by the always-mounted `AuthProvider`, not by this hook) when the
            // confirm resolves.
            const initiatingDid = session.did
            void confirmPinnedPostCommit(wasPinned ? null : post.uri, {
                identifier,
                publicAgent,
                session,
                limit: PROFILE_FEED_PAGE_SIZE,
            })
                .then(confirmedPage => {
                    // A newer pin/unpin superseded this one for this same query key — its
                    // own chain owns the cache write, and applying this stale expectation
                    // would undo it. Keyed off the query key (not this component mount), so
                    // an orphaned chain from an unmounted/remounted instance can never win
                    // against a newer chain for the same post/query key.
                    if (currentPinEpoch(pinSyncKey) !== context?.pinEpoch) return
                    if (confirmedPage) {
                        // The poll can resolve up to ~56s after the pin was initiated. If the
                        // active account changed in the meantime, this confirmed page carries
                        // the *previous* account's viewer state (like/repost/bookmark/pin
                        // URIs) and must never be written into whatever account's cache entry
                        // this query key now resolves to — discard it instead. This is an
                        // expected occurrence on account switch, not an error.
                        const activeDid = getActiveBlueskySessionDid()
                        if (activeDid !== initiatingDid) {
                            // Close immediately rather than on the grace below: there is no
                            // confirmed page to protect, and `reconcilePendingPin` is already
                            // suppressing this window for the now-active account anyway.
                            endPinOperation(pinSyncKey, context?.pinEpoch)
                            console.warn(
                                '[usePostEngagement] discarding pin confirm result: active account changed since the pin was initiated',
                                { initiatingDid, activeDid }
                            )
                            return
                        }
                        // Page one is where the pinned slot lives and the only page the pin
                        // reorders; later pages keep their cursors so the user's scroll depth
                        // survives. Nothing is written if the feed was never loaded.
                        queryClient.setQueryData<InfiniteData<PostsPage>>(
                            pinnedFeedKey,
                            oldData => {
                                if (!oldData?.pages?.length) return oldData
                                return {
                                    ...oldData,
                                    pages: [confirmedPage, ...oldData.pages.slice(1)],
                                }
                            }
                        )
                        /**
                         * Confirmed — but do not close the window in the same breath.
                         *
                         * A fetch of this key that *started* before this moment (an
                         * unrelated engagement mutation's `onError` invalidating the
                         * `['posts', identifier]` prefix, a reconnect, a remount on this
                         * `staleTime: 0` feed) is carrying genuinely pre-pin bytes and can
                         * still be in flight. Closing here leaves it unreconciled, and it
                         * lands straight over the confirmed page — silently reverting a
                         * pin the user has been told succeeded, with nothing left to
                         * repair it. `onMutate`'s `cancelQueries` does not reach it: it
                         * only cancelled what was in flight at click time.
                         *
                         * Fetches that *start* after this are fine — page one of the own
                         * pinned slot is read through a cache-bypassing agent, so they are
                         * origin reads of an AppView that has just been proven to show the
                         * pin. So this only has to outlast a straggler, not stay open.
                         */
                        scheduleEndPinOperation(
                            pinSyncKey,
                            context?.pinEpoch,
                            PIN_CONFIRM_RECONCILE_GRACE_MS
                        )
                        return
                    }
                    // Timed out: terminal, so the window closes now. The user has been told
                    // to expect a delay, and a window left open would re-apply this
                    // expectation over every future refetch, masking a pin made elsewhere.
                    endPinOperation(pinSyncKey, context?.pinEpoch)
                    console.warn(
                        '[usePostEngagement] pinnedPost not visible on the AppView feed after all confirm rounds; keeping optimistic pin state, not refetching'
                    )
                    // The window is long enough now that a silent slow confirm would look
                    // like nothing happened — say so instead.
                    showToast(
                        wasPinned ? strings.posts.unpinPendingSync : strings.posts.pinPendingSync,
                        'info'
                    )
                })
                .catch(err => {
                    // The confirm poll itself blew up (a network error the poller did not
                    // swallow, or a throw out of the cache write above). Nothing else will
                    // close this window, so close it here rather than leave the feed
                    // permanently reconciled against an expectation no one is tracking.
                    endPinOperation(pinSyncKey, context?.pinEpoch)
                    console.error('[usePostEngagement] pin confirmation chain failed:', err)
                })
        },
        onError: (error, post, context) => {
            console.error('[usePostEngagement] pin toggle failed:', error)
            // Close the reconcile window *before* restoring the snapshot, so neither the
            // rollback nor the resync refetch below is itself reconciled back into the
            // state being rolled back from.
            if (context) endPinOperation(context.pinSyncKey, context.pinEpoch)
            if (context?.previousFeedData) {
                for (const [key, data] of context.previousFeedData) {
                    queryClient.setQueryData(key, data)
                }
            }
            // Mastodon answers 422 for every pin rejection (limit reached, ineligible
            // status, not the owner's) with a localised body, so the status code is all
            // we can key off — hence one generic message covering every cause.
            const rejected = error instanceof MastodonStatusActionError && error.status === 422
            // A stale unpin: the PDS says something other than this post is pinned, so
            // the write was deliberately not made. Reporting the optimistic "unpinned"
            // as success would leave the UI claiming an empty pinned slot while the
            // profile still shows a pin. Say it failed and resync below.
            const stale = error instanceof StalePinnedPostError
            showToast(
                stale
                    ? strings.posts.pinOutOfDate
                    : rejected
                      ? strings.posts.pinRejected
                      : post.isPinned
                        ? strings.posts.couldNotUnpin
                        : strings.posts.couldNotPin,
                'error'
            )
            void queryClient.invalidateQueries({ queryKey })
        },
    })

    return {
        toggleLike: (post: Post) => toggleLike.mutate(post),
        toggleRepost: (post: Post) => toggleRepost.mutate(post),
        toggleBookmark: (post: Post) => toggleBookmark.mutate(post),
        removePost,
        togglePin,
        isLiking: toggleLike.isPending,
        isReposting: toggleRepost.isPending,
        isBookmarking: toggleBookmark.isPending,
        isDeleting: removePost.isPending,
    }
}
